/**
 * Regelwerk — the per-tenant Schema + Soll-Deckung catalog contract (P2 Placement Slice 3).
 *
 * Slice 1/2 shipped the Kunde/Police field + Soll catalogs as CODE (kunde-risikoprofil.ts,
 * police-risiko.ts). Slice 3 persists them per tenant in two tables:
 *   - branch_attribute_schema  — the atomic field schema (ebene 'police' per IGB2B code, ebene
 *                                'kunde' per Risikoprofil-Typ + Kategorie), multilingual labels.
 *   - target_coverage_catalog  — the Regelwerk (ebene 'police' = Soll-Deckung rules, ebene 'kunde'
 *                                = Basissparten-Checkliste), multilingual labels + ziel/kriterium.
 *
 * The CODE catalog stays the canonical DEFAULT; the DB rows are the runtime truth. A per-tenant
 * boot + provisioning seed (RegelwerkSeedService) writes the code catalogs into the tables and
 * re-seeds an un-customized system row only when the catalog fingerprint changes. The Slice-1/2
 * services read through RegelwerkResolverService (DB rows if the slice has any, else the code
 * catalog) so adding/editing a field or a rule needs no deploy.
 *
 * This module is the SINGLE wire + mapper seam shared by api (seed/resolver/admin) and web (admin
 * page). It carries NO SQL and NO crypto — the fingerprint is a stable string the api sha1s.
 *
 * [Source: docs/product/p2-slices/slice-3-schema-regelwerk-design.md §3.1]
 * [Source: packages/shared/src/kunde-risikoprofil.ts + police-risiko.ts (the code catalogs seeded FROM)]
 */

import {
  BRANCHEN_IST_KATALOG,
  SOLL_DECKUNG_KATALOG,
  policeRisikoOptionLabel,
} from './police-risiko';
import type { BranchenFeldDefinition, SollDeckungRegel, SollPflichtStatus } from './police-risiko';
import {
  BASISSPARTEN_CHECKLISTE,
  RISIKOPROFIL_TYP_VALUES,
  risikoprofilFelderFuer,
  risikoprofilOptionLabel,
} from './kunde-risikoprofil';
import type {
  RisikoprofilFeldDefinition,
  RisikoprofilFeldTyp,
  RisikoprofilKategorie,
  RisikoprofilTyp,
  BasisspartenDefinition,
  BasisspartenStatus,
} from './kunde-risikoprofil';

// ── Sprachen + Ebene + Origin vocab ──────────────────────────────────────────────

/** The four Regelwerk label languages. DE is always present; FR/IT/EN are Slice 3b (seeded DE only). */
export const REGELWERK_SPRACHEN = ['de', 'fr', 'it', 'en'] as const;
export type RegelwerkSprache = (typeof REGELWERK_SPRACHEN)[number];
export const REGELWERK_DEFAULT_SPRACHE: RegelwerkSprache = 'de';

/** A multilingual text blob. `de` is mandatory (DB CHECK label_i18n ? 'de'); the rest are optional. */
export interface I18nText {
  de: string;
  fr?: string;
  it?: string;
  en?: string;
}

/** Which level a schema field / catalog rule belongs to. */
export const REGELWERK_EBENE = {
  KUNDE: 'kunde',
  POLICE: 'police',
} as const;
export type RegelwerkEbene = (typeof REGELWERK_EBENE)[keyof typeof REGELWERK_EBENE];
export const REGELWERK_EBENE_VALUES: readonly RegelwerkEbene[] = [
  REGELWERK_EBENE.KUNDE,
  REGELWERK_EBENE.POLICE,
];

/** Where a row came from: a seeded code default ('system') or an admin creation ('custom'). */
export const REGELWERK_ORIGIN = {
  SYSTEM: 'system',
  CUSTOM: 'custom',
} as const;
export type RegelwerkOrigin = (typeof REGELWERK_ORIGIN)[keyof typeof REGELWERK_ORIGIN];
export const REGELWERK_ORIGIN_VALUES: readonly RegelwerkOrigin[] = [
  REGELWERK_ORIGIN.SYSTEM,
  REGELWERK_ORIGIN.CUSTOM,
];

/** One select/multiselect option: the stored value + its German label (FR/IT/EN deferred to 3b). */
export interface RegelwerkOption {
  value: string;
  label: string;
}

// ── Pure label helpers ───────────────────────────────────────────────────────────

/** Pick the label in `sprache`, falling back to `de` (always present) when the translation is absent. */
export function pickLabel(text: I18nText, sprache: RegelwerkSprache = REGELWERK_DEFAULT_SPRACHE): string {
  return text[sprache] ?? text.de;
}

/** Type guard: a jsonb value shaped like an I18nText (an object with a string `de`). */
export function isI18nText(value: unknown): value is I18nText {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as Record<string, unknown>).de === 'string'
  );
}

/** Extract the option VALUES (string[]) from a stored options_json array (objects or bare strings). */
export function optionValuesFromJson(json: RegelwerkOption[] | null | undefined): readonly string[] | undefined {
  if (json == null) return undefined;
  return json.map((o) => (typeof o === 'string' ? (o as string) : o.value));
}

// ── Wire row shapes (snake_case; what the repository returns, jsonb coerced to objects) ───

/** One branch_attribute_schema row. */
export interface BranchAttributeSchemaRow {
  id: string;
  tenant_id: string;
  ebene: RegelwerkEbene;
  branchencode: string | null;
  profil_typ: RisikoprofilTyp | null;
  kategorie: string | null;
  feld_key: string;
  typ: RisikoprofilFeldTyp;
  label_i18n: I18nText;
  options_json: RegelwerkOption[] | null;
  einheit: string | null;
  hinweis_i18n: I18nText | null;
  pflicht: boolean;
  sort_order: number;
  aktiv: boolean;
  origin: RegelwerkOrigin;
  catalog_version: string | null;
  customized_at: string | null;
  updated_at: string;
}

/** One target_coverage_catalog row. */
export interface TargetCoverageRuleRow {
  id: string;
  tenant_id: string;
  ebene: RegelwerkEbene;
  rule_key: string;
  branchencode: string;
  kundentyp: RisikoprofilTyp | null;
  label_i18n: I18nText;
  pflicht_status: SollPflichtStatus;
  rechtsgrundlage: string | null;
  ziel_text_i18n: I18nText;
  kriterium_i18n: I18nText | null;
  segment_criteria_json: Record<string, unknown> | null;
  ziel_deckungssumme: string | null;
  prioritaet: number;
  aktiv: boolean;
  origin: RegelwerkOrigin;
  catalog_version: string | null;
  customized_at: string | null;
  updated_at: string;
}

// ── Resolved shapes (the code catalog types + the DB id/overrides) ────────────────

/** A Police Ist-field resolved from the DB (or the code catalog): the code shape + the schema id. */
export interface ResolvedBranchenFeld extends BranchenFeldDefinition {
  /** The branch_attribute_schema row id, or null for a code-catalog fallback field. */
  schema_id: string | null;
  pflicht: boolean;
}

/** A Kunde Risikoprofil-field resolved from the DB (or the code catalog). */
export interface ResolvedKundeFeld extends RisikoprofilFeldDefinition {
  schema_id: string | null;
  pflicht: boolean;
}

/** A Soll-Deckung rule resolved from the DB (or the code catalog): the code shape + the catalog id. */
export interface ResolvedSollRegel extends SollDeckungRegel {
  /** The target_coverage_catalog row id (the policy_target_coverage.rule_id FK), or null for code. */
  rule_id: string | null;
}

/** A Kunde Basissparte resolved from the DB (or the code catalog). */
export interface ResolvedBasissparte extends BasisspartenDefinition {
  rule_id: string | null;
}

// ── Mappers: DB row → resolved shape ──────────────────────────────────────────────

/** Map a 'police' schema row to a ResolvedBranchenFeld in the given Sprache. */
export function schemaRowToBranchenFeld(
  row: BranchAttributeSchemaRow,
  sprache: RegelwerkSprache = REGELWERK_DEFAULT_SPRACHE,
): ResolvedBranchenFeld {
  return {
    key: row.feld_key,
    label: pickLabel(row.label_i18n, sprache),
    typ: row.typ,
    options: optionValuesFromJson(row.options_json),
    einheit: row.einheit ?? undefined,
    hinweis: row.hinweis_i18n ? pickLabel(row.hinweis_i18n, sprache) : undefined,
    branchencode: row.branchencode ?? '',
    schema_id: row.id,
    pflicht: row.pflicht,
  };
}

/** Map a 'kunde' schema row to a ResolvedKundeFeld in the given Sprache. */
export function schemaRowToKundeFeld(
  row: BranchAttributeSchemaRow,
  sprache: RegelwerkSprache = REGELWERK_DEFAULT_SPRACHE,
): ResolvedKundeFeld {
  return {
    key: row.feld_key,
    label: pickLabel(row.label_i18n, sprache),
    typ: row.typ,
    options: optionValuesFromJson(row.options_json),
    einheit: row.einheit ?? undefined,
    hinweis: row.hinweis_i18n ? pickLabel(row.hinweis_i18n, sprache) : undefined,
    profil_typ: (row.profil_typ ?? 'firma') as RisikoprofilTyp,
    kategorie: (row.kategorie ?? '') as RisikoprofilKategorie,
    schema_id: row.id,
    pflicht: row.pflicht,
  };
}

/** Map a 'police' catalog row to a ResolvedSollRegel in the given Sprache. */
export function ruleRowToSollRegel(
  row: TargetCoverageRuleRow,
  sprache: RegelwerkSprache = REGELWERK_DEFAULT_SPRACHE,
): ResolvedSollRegel {
  return {
    rule_key: row.rule_key,
    branchencode: row.branchencode,
    label: pickLabel(row.label_i18n, sprache),
    pflicht_status: row.pflicht_status,
    rechtsgrundlage: row.rechtsgrundlage ?? undefined,
    ziel_text: pickLabel(row.ziel_text_i18n, sprache),
    ziel_default_chf: row.ziel_deckungssumme != null ? Number(row.ziel_deckungssumme) : undefined,
    rule_id: row.id,
  };
}

/** Map a 'kunde' catalog row to a ResolvedBasissparte in the given Sprache. */
export function ruleRowToBasissparte(
  row: TargetCoverageRuleRow,
  sprache: RegelwerkSprache = REGELWERK_DEFAULT_SPRACHE,
): ResolvedBasissparte {
  return {
    sparte_key: row.rule_key,
    profil_typ: (row.kundentyp ?? 'firma') as RisikoprofilTyp,
    branchencode: row.branchencode,
    label: pickLabel(row.label_i18n, sprache),
    // The kunde catalog only ever carries pflicht/bedingt_pflicht/empfehlung (never 'optional').
    default_status: row.pflicht_status as BasisspartenStatus,
    kriterium: row.kriterium_i18n ? pickLabel(row.kriterium_i18n, sprache) : '',
    richtwert: pickLabel(row.ziel_text_i18n, sprache),
    rule_id: row.id,
  };
}

// ── Mappers: code catalog entry → resolved shape (the code-only fallback) ──────────

export function codeFeldToResolved(f: BranchenFeldDefinition): ResolvedBranchenFeld {
  return { ...f, schema_id: null, pflicht: false };
}
export function codeKundeFeldToResolved(f: RisikoprofilFeldDefinition): ResolvedKundeFeld {
  return { ...f, schema_id: null, pflicht: false };
}
export function codeRegelToResolved(r: SollDeckungRegel): ResolvedSollRegel {
  return { ...r, rule_id: null };
}
export function codeBasisspartToResolved(b: BasisspartenDefinition): ResolvedBasissparte {
  return { ...b, rule_id: null };
}

// ── Seed inputs (the code catalogs flattened into table rows) ─────────────────────

/** One branch_attribute_schema row the seed upserts (origin 'system'). */
export interface SchemaSeedRow {
  ebene: RegelwerkEbene;
  branchencode: string | null;
  profil_typ: RisikoprofilTyp | null;
  kategorie: RisikoprofilKategorie | null;
  feld_key: string;
  typ: RisikoprofilFeldTyp;
  label_i18n: I18nText;
  options_json: RegelwerkOption[] | null;
  einheit: string | null;
  hinweis_i18n: I18nText | null;
  sort_order: number;
}

/** One target_coverage_catalog row the seed upserts (origin 'system'). */
export interface RuleSeedRow {
  ebene: RegelwerkEbene;
  rule_key: string;
  branchencode: string;
  kundentyp: RisikoprofilTyp | null;
  label_i18n: I18nText;
  pflicht_status: SollPflichtStatus;
  rechtsgrundlage: string | null;
  ziel_text_i18n: I18nText;
  kriterium_i18n: I18nText | null;
  segment_criteria_json: Record<string, unknown> | null;
  ziel_deckungssumme: number | null;
  prioritaet: number;
}

/** Base prioritaet for a seeded Soll rule (prioritaet = REGELWERK_SEED_PRIO_BASE + catalog index). */
export const REGELWERK_SEED_PRIO_BASE = 100;

/** Build the select/multiselect options_json (value + German label) for a code field, or null. */
function seedOptions(
  options: readonly string[] | undefined,
  labelOf: (v: string) => string,
): RegelwerkOption[] | null {
  if (options == null) return null;
  return options.map((value) => ({ value, label: labelOf(value) }));
}

/**
 * Flatten the Slice-1/2 code catalogs into seedable rows: 162 police + 65 kunde schema (227),
 * 57 police + 18 kunde rules (75). The ONE source of truth the seed + the fingerprint read.
 */
export function regelwerkSeedRows(): { schema: SchemaSeedRow[]; rules: RuleSeedRow[] } {
  const schema: SchemaSeedRow[] = [];

  // Police field schema — one row per BRANCHEN_IST_KATALOG field (sort_order = index within code).
  for (const [code, felder] of Object.entries(BRANCHEN_IST_KATALOG)) {
    felder.forEach((f, i) => {
      schema.push({
        ebene: REGELWERK_EBENE.POLICE,
        branchencode: code,
        profil_typ: null,
        kategorie: null,
        feld_key: f.key,
        typ: f.typ,
        label_i18n: { de: f.label },
        options_json: seedOptions(f.options, policeRisikoOptionLabel),
        einheit: f.einheit ?? null,
        hinweis_i18n: f.hinweis ? { de: f.hinweis } : null,
        sort_order: i,
      });
    });
  }

  // Kunde field schema — one row per RISIKOPROFIL_FELD_KATALOG field (sort_order = index within Typ).
  for (const typ of RISIKOPROFIL_TYP_VALUES) {
    const felder = risikoprofilFelderFuer(typ);
    felder.forEach((f, i) => {
      schema.push({
        ebene: REGELWERK_EBENE.KUNDE,
        branchencode: null,
        profil_typ: f.profil_typ,
        kategorie: f.kategorie,
        feld_key: f.key,
        typ: f.typ,
        label_i18n: { de: f.label },
        options_json: seedOptions(f.options, risikoprofilOptionLabel),
        einheit: f.einheit ?? null,
        hinweis_i18n: f.hinweis ? { de: f.hinweis } : null,
        sort_order: i,
      });
    });
  }

  const rules: RuleSeedRow[] = [];

  // Police Soll-Deckung rules — one row per SOLL_DECKUNG_KATALOG entry.
  SOLL_DECKUNG_KATALOG.forEach((r, i) => {
    rules.push({
      ebene: REGELWERK_EBENE.POLICE,
      rule_key: r.rule_key,
      branchencode: r.branchencode,
      kundentyp: null,
      label_i18n: { de: r.label },
      pflicht_status: r.pflicht_status,
      rechtsgrundlage: r.rechtsgrundlage ?? null,
      ziel_text_i18n: { de: r.ziel_text },
      kriterium_i18n: null,
      segment_criteria_json: null,
      ziel_deckungssumme: r.ziel_default_chf ?? null,
      prioritaet: REGELWERK_SEED_PRIO_BASE + i,
    });
  });

  // Kunde Basissparten-Checkliste — one row per BASISSPARTEN_CHECKLISTE entry.
  BASISSPARTEN_CHECKLISTE.forEach((b, j) => {
    rules.push({
      ebene: REGELWERK_EBENE.KUNDE,
      rule_key: b.sparte_key,
      branchencode: b.branchencode,
      kundentyp: b.profil_typ,
      label_i18n: { de: b.label },
      pflicht_status: b.default_status,
      rechtsgrundlage: null,
      ziel_text_i18n: { de: b.richtwert },
      kriterium_i18n: { de: b.kriterium },
      segment_criteria_json: null,
      ziel_deckungssumme: null,
      prioritaet: REGELWERK_SEED_PRIO_BASE + j,
    });
  });

  return { schema, rules };
}

// ── Catalog fingerprint (the seed re-seeds only when this changes) ────────────────

/** Deterministic JSON: object keys sorted recursively so the fingerprint is stable across runs. */
export function stableStringify(value: unknown): string {
  return JSON.stringify(sortDeep(value));
}

function sortDeep(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sortDeep);
  if (value !== null && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(value as Record<string, unknown>).sort()) {
      out[key] = sortDeep((value as Record<string, unknown>)[key]);
    }
    return out;
  }
  return value;
}

/**
 * The canonical fingerprint STRING of the current code catalogs. The api sha1s this to the
 * `catalog_version`; a code change flips the hash and re-seeds un-customized system rows.
 */
export function regelwerkCatalogFingerprint(): string {
  return stableStringify(regelwerkSeedRows());
}

// ── Admin DTO bodies + list response (wire shapes; class-validator DTOs live in the api) ──

/** POST /admin/regelwerk/soll-deckung — create one custom Soll rule. */
export interface SollDeckungRuleCreateBody {
  ebene: RegelwerkEbene;
  rule_key: string;
  branchencode: string;
  kundentyp?: RisikoprofilTyp | null;
  label_i18n: I18nText;
  pflicht_status: SollPflichtStatus;
  rechtsgrundlage?: string | null;
  ziel_text_i18n: I18nText;
  kriterium_i18n?: I18nText | null;
  ziel_deckungssumme?: number | null;
  prioritaet?: number;
}

/** PATCH /admin/regelwerk/soll-deckung/:id — merge-patch of one Soll rule's editable fields. */
export interface SollDeckungRulePatchBody {
  label_i18n?: I18nText;
  pflicht_status?: SollPflichtStatus;
  rechtsgrundlage?: string | null;
  ziel_text_i18n?: I18nText;
  kriterium_i18n?: I18nText | null;
  ziel_deckungssumme?: number | null;
  prioritaet?: number;
  aktiv?: boolean;
  expected_updated_at?: string;
}

/** PATCH /admin/regelwerk/attribut-schema/:id — the limited schema-field PATCH (Slice 3). */
export interface AttributSchemaPatchBody {
  label_i18n?: I18nText;
  hinweis_i18n?: I18nText | null;
  einheit?: string | null;
  pflicht?: boolean;
  sort_order?: number;
  aktiv?: boolean;
  expected_updated_at?: string;
}

/** One Soll-Deckung catalog item on the admin wire (resolved label/ziel_text + the raw i18n). */
export interface RegelwerkSollDeckungItem {
  id: string;
  ebene: RegelwerkEbene;
  rule_key: string;
  branchencode: string;
  kundentyp: RisikoprofilTyp | null;
  label: string;
  label_i18n: I18nText;
  pflicht_status: SollPflichtStatus;
  rechtsgrundlage: string | null;
  ziel_text: string;
  ziel_text_i18n: I18nText;
  kriterium_i18n: I18nText | null;
  ziel_deckungssumme: string | null;
  prioritaet: number;
  aktiv: boolean;
  origin: RegelwerkOrigin;
  customized_at: string | null;
  updated_at: string;
}

/** One attribut-schema item on the admin wire (resolved label/hinweis + the raw i18n). */
export interface RegelwerkAttributSchemaItem {
  id: string;
  ebene: RegelwerkEbene;
  branchencode: string | null;
  profil_typ: RisikoprofilTyp | null;
  kategorie: string | null;
  feld_key: string;
  typ: RisikoprofilFeldTyp;
  label: string;
  label_i18n: I18nText;
  options_json: RegelwerkOption[] | null;
  einheit: string | null;
  hinweis_i18n: I18nText | null;
  pflicht: boolean;
  sort_order: number;
  aktiv: boolean;
  origin: RegelwerkOrigin;
  customized_at: string | null;
  updated_at: string;
}

/** The admin list envelope (the resolved sprache echoes back so the UI knows which labels it got). */
export interface RegelwerkListResponse<T> {
  items: T[];
  sprache: RegelwerkSprache;
}

/** Domain/audit tags the Regelwerk emits (names honored by the api audit-actions). */
export const REGELWERK_EVENTS = {
  SEED: 'regelwerk.seeded',
} as const;
